import React, { useEffect, useState } from "react";
import { DollarSign, RefreshCcw, Mail, UploadCloud, Users, Activity } from "lucide-react";
import {
    fetchMaxDailyMessages,
    updateMaxDailyMessages,
    fetchCurrentMonthAiCosts,
    fetchSpeechToTextSetting,
    updateSpeechToTextSetting,
    fetchAllowManagersCreateUsersSetting,
    updateAllowManagersCreateUsersSetting,
    fetchSystemEmailSetting,
    updateSystemEmailSetting,
    testSystemEmail,
    updateAdminNotificationSettings
} from "../../services/api";
import { AiCostSummary } from "../../types";
import { useSettings } from "../../contexts/SettingsContext";
import AdminNotificationTester from "../../components/AdminNotificationTester";
import Preloader from "../../components/Preloder";

export const AdminSettings: React.FC = () => {
    const { speechToTextEnabled, updateSpeechToTextEnabled, notificationsEnabled, updateNotificationsEnabled } = useSettings();
    const [speechToText, setSpeechToText] = useState(false);
    const [allowManagers, setAllowManagers] = useState(false);
    const [email, setEmail] = useState("");
    const [previousEmail, setPreviousEmail] = useState("");
    const [maxSessions, setMaxSessions] = useState<string>("");
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saveSuccess, setSaveSuccess] = useState<string | null>(null);

    // Email-specific state
    const [emailSaving, setEmailSaving] = useState(false);
    const [emailTesting, setEmailTesting] = useState(false);
    const [emailError, setEmailError] = useState<string | null>(null);
    const [emailSuccess, setEmailSuccess] = useState<string | null>(null);

    // AI Cost tracking state
    const [aiCosts, setAiCosts] = useState<AiCostSummary | null>(null);
    const [aiCostsLoading, setAiCostsLoading] = useState(false);
    const [aiCostsError, setAiCostsError] = useState<string | null>(null);
    const [lastUpdated, setLastUpdated] = useState<string | null>(null);

    // Load AI costs
    const loadAiCosts = async () => {
        try {
            setAiCostsLoading(true);
            setAiCostsError(null);
            const costs = await fetchCurrentMonthAiCosts();
            setAiCosts(costs);
            setLastUpdated(new Date().toLocaleTimeString());
        } catch (e: any) {
            setAiCostsError(e?.message || "Failed to load AI costs");
        } finally {
            setAiCostsLoading(false);
        }
    };

    // Load saved values from backend on mount
    useEffect(() => {
        let cancelled = false;
        async function load() {
            try {
                setLoading(true);
                setLoadError(null);

                // Load max daily messages
                const res = await fetchMaxDailyMessages();
                const value = res?.value;
                if (!cancelled) {
                    setMaxSessions(value == null ? "" : String(value));
                }

                // Load speech-to-text setting
                const speechRes = await fetchSpeechToTextSetting();
                if (!cancelled) {
                    setSpeechToText(speechRes?.enabled || false);
                }

                // Load allow managers create users setting
                const managersRes = await fetchAllowManagersCreateUsersSetting();
                if (!cancelled) {
                    setAllowManagers(managersRes?.enabled || false);
                }

                // Load system email setting
                const emailRes = await fetchSystemEmailSetting();
                if (!cancelled) {
                    setEmail(emailRes?.email || "");
                    setPreviousEmail(emailRes?.email || "");
                }
            } catch (e: any) {
                if (!cancelled) setLoadError(e?.message || "Failed to load settings");
            } finally {
                if (!cancelled) setLoading(false);
            }
        }
        load();
        return () => { cancelled = true; };
    }, []);

    // Sync local speech-to-text state with SettingsContext
    useEffect(() => {
        setSpeechToText(speechToTextEnabled);
    }, [speechToTextEnabled]);

    // Load AI costs on mount - removed auto-refresh to prevent unnecessary API calls
    // Users can manually refresh using the refresh button if needed
    useEffect(() => {
        loadAiCosts();
    }, []);

    // Show preloader while data is loading
    if (loading) {
        return <Preloader />;
    }

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Feature */}
            <div className="setting-card">
                <div className="flex items-center justify-between mb-4">
                    <h4 className="grey-900">Feature</h4>
                    <div className="setting-card-icon">
                        <svg width="34" height="34" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12.5203 24.318L13.7722 19.0246L9.6899 15.5138L15.065 15.0103L17.1742 10.0979L19.2834 15.0647L24.6584 15.5138L20.5761 19.0246L21.828 24.318L17.1742 21.5148L12.5203 24.318ZM17.1742 3.56616C18.9612 3.56616 20.7307 3.91814 22.3816 4.60199C24.0326 5.28584 25.5327 6.28818 26.7963 7.55178C28.0599 8.81538 29.0622 10.3155 29.7461 11.9665C30.4299 13.6174 30.7819 15.3869 30.7819 17.1739C30.7819 20.7829 29.3482 24.2441 26.7963 26.7961C24.2443 29.348 20.7832 30.7817 17.1742 30.7817C15.3872 30.7817 13.6177 30.4297 11.9667 29.7458C10.3157 29.062 8.81562 28.0596 7.55203 26.7961C5.00008 24.2441 3.56641 20.7829 3.56641 17.1739C3.56641 13.5649 5.00008 10.1037 7.55203 7.55178C10.104 4.99983 13.5652 3.56616 17.1742 3.56616ZM17.1742 6.28771C14.287 6.28771 11.518 7.43465 9.47645 9.47621C7.43489 11.5178 6.28796 14.2867 6.28796 17.1739C6.28796 20.0611 7.43489 22.8301 9.47645 24.8716C11.518 26.9132 14.287 28.0601 17.1742 28.0601C20.0614 28.0601 22.8303 26.9132 24.8719 24.8716C26.9134 22.8301 28.0604 20.0611 28.0604 17.1739C28.0604 14.2867 26.9134 11.5178 24.8719 9.47621C22.8303 7.43465 20.0614 6.28771 17.1742 6.28771Z" fill="#DA291C" />
                        </svg>
                    </div>
                </div>
                <div className="space-y-4 text-sm">
                    <ToggleRow
                        label="Enable Speech to Text"
                        enabled={speechToText}
                        onToggle={async () => {
                            const newValue = !speechToText;
                            setSpeechToText(newValue);
                            updateSpeechToTextEnabled(newValue); // Update context immediately
                            try {
                                await updateSpeechToTextSetting(newValue);
                            } catch (e: any) {
                                setSpeechToText(!newValue); // Revert on error
                                updateSpeechToTextEnabled(!newValue); // Revert context on error
                                setSaveError(e?.message || "Failed to update speech-to-text setting");
                            }
                        }}
                    />
                    <ToggleRow
                        label="Allow Managers to Create Users"
                        enabled={allowManagers}
                        onToggle={async () => {
                            const newValue = !allowManagers;
                            setAllowManagers(newValue);
                            try {
                                await updateAllowManagersCreateUsersSetting(newValue);
                            } catch (e: any) {
                                setAllowManagers(!newValue); // Revert on error
                                setSaveError(e?.message || "Failed to update allow-managers setting");
                            }
                        }}
                    />
                    <ToggleRow
                        label="Enable Notification"
                        enabled={notificationsEnabled}
                        onToggle={async () => {
                            const newValue = !notificationsEnabled;
                            updateNotificationsEnabled(newValue);
                            try {
                                await updateAdminNotificationSettings(newValue);
                            } catch (e: any) {
                                updateNotificationsEnabled(!newValue); // Revert on error
                                setSaveError(e?.message || "Failed to update notification setting");
                            }
                        }}
                    />
                </div>
            </div>

            {/* AI Usage */}
            <div className="setting-card">
                <div className="flex items-center justify-between mb-2">
                    <div>
                        <h4 className="grey-900">AI Usage (USD)</h4>
                        <p className="text-xs text-gray-500">
                            Current Month ({aiCosts?.period || 'Loading...'})
                        </p>
                        {lastUpdated && (
                            <p className="text-xs text-gray-400">Updated: {lastUpdated}</p>
                        )}
                    </div>
                    <div className="setting-card-icon">
                        <svg width="34" height="34" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <g clip-path="url(#clip0_576_2937)">
                                <path d="M13.0917 0.844727V4.92705H10.03C7.25398 4.92705 4.92705 7.25398 4.92705 10.03V12.0711C4.92705 14.8471 6.72328 17.1332 9.37679 17.8272L19.8275 20.5215C20.3991 20.6848 21.2155 21.7054 21.2155 22.3178V24.3589C21.2155 24.9305 20.7665 25.3795 20.195 25.3795H9.98914C9.63455 25.3781 9.28515 25.2943 8.96856 25.1346V21.2972H4.88623V25.3795C4.88623 26.7675 5.7027 27.9514 6.68245 28.5637C7.62139 29.2169 8.80526 29.4618 9.98914 29.4618H13.0509V33.5442H17.1332V29.4618H20.195C23.0118 29.4618 25.2979 27.1757 25.2979 24.3589V22.3178C25.2979 19.5418 23.5016 17.2557 20.8481 16.5617L10.3974 13.8673C9.82584 13.7041 9.00938 12.6835 9.00938 12.0711V10.03C9.00938 9.45843 9.45844 9.00938 10.03 9.00938H20.2358C20.6848 9.00938 21.0931 9.17267 21.2564 9.25432V13.0917H25.3387V9.00938C25.3387 7.62139 24.5222 6.43751 23.5425 5.82516C22.6035 5.17199 21.4197 4.92705 20.2358 4.92705H17.174V0.844727H13.0917Z" fill="#DA291C" />
                            </g>
                            <defs>
                                <clipPath id="clip0_576_2937">
                                    <rect width="32.6586" height="32.6586" fill="white" transform="translate(0.844727 0.844727)" />
                                </clipPath>
                            </defs>
                        </svg>
                    </div>
                </div>

                {aiCostsLoading ? (
                    <div className="mt-6 flex items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
                        <span className="ml-2 text-sm text-gray-500">Loading costs...</span>
                    </div>
                ) : aiCostsError ? (
                    <div className="mt-6">
                        <div className="text-red-600 text-sm mb-2">{aiCostsError}</div>
                        <button
                            onClick={loadAiCosts}
                            className="text-xs text-blue-600 hover:text-blue-800 underline"
                        >
                            Retry
                        </button>
                    </div>
                ) : aiCosts ? (
                    <div className="mt-6">
                        <div className="text-3xl font-extrabold text-gray-800">
                            ${aiCosts.total_cost.toFixed(4)}
                        </div>

                        {/* Additional metrics */}
                        <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                            <div className="flex items-center gap-2">
                                <Activity size={16} className="text-gray-400" />
                                <span className="text-gray-600">{aiCosts.total_requests} requests</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Users size={16} className="text-gray-400" />
                                <span className="text-gray-600">{aiCosts.unique_users_count} users</span>
                            </div>
                        </div>

                        {/* Token usage */}
                        <div className="mt-3 text-xs text-gray-500">
                            {aiCosts.total_tokens.toLocaleString()} tokens used
                        </div>

                        {/* Refresh button */}
                        <button
                            onClick={loadAiCosts}
                            className="mt-3 text-xs text-blue-600 hover:text-blue-800 underline"
                        >
                            Refresh
                        </button>
                    </div>
                ) : (
                    <div className="mt-6 text-3xl font-extrabold text-gray-400">$0.00</div>
                )}
            </div>

            {/* System Email Setting */}
            <div className="setting-card">
                <div className="flex items-center justify-between mb-4">
                    <h4 className="grey-900">System Email Setting</h4>
                    <div className="setting-card-icon">
                        <svg width="34" height="34" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M30.7819 9.00939C30.7819 7.51254 29.5572 6.28784 28.0604 6.28784H6.28796C4.7911 6.28784 3.56641 7.51254 3.56641 9.00939V25.3387C3.56641 26.8356 4.7911 28.0602 6.28796 28.0602H28.0604C29.5572 28.0602 30.7819 26.8356 30.7819 25.3387V9.00939ZM28.0604 9.00939L17.1742 15.8133L6.28796 9.00939H28.0604ZM28.0604 25.3387H6.28796V11.7309L17.1742 18.5348L28.0604 11.7309V25.3387Z" fill="#DA291C" />
                        </svg>
                    </div>
                </div>
                <div className="form-group">
                    <label>Email ID</label>
                    <input
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter Email"
                        className="form-control"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                        This email will receive password reset and setup emails. Test emails will be sent TO this address FROM your configured SMTP email.
                    </p>
                </div>
                <div className="mt-4 flex gap-2 items-center justify-end">
                <button
                    onClick={async () => {
                        try {
                            setEmailTesting(true);
                            setEmailError(null);
                            setEmailSuccess(null);

                            // If email changed, save first
                            if (email.trim() !== previousEmail.trim()) {
                                await updateSystemEmailSetting(email);
                                setPreviousEmail(email);
                            }

                            // Then test
                            const result = await testSystemEmail();
                            if (result.email_sent) {
                                setEmailSuccess(`Test email sent successfully to ${email}!`);
                            } else {
                                setEmailError("Failed to send test email");
                            }
                        } catch (e: any) {
                            setEmailError(e?.message || "Failed to send test email");
                        } finally {
                            setEmailTesting(false);
                        }
                    }}
                    className="renegade-btn-primary mini-btn"
                    disabled={emailTesting || !email.trim()}
                >
                    <UploadCloud size={18} />
                    {emailTesting ? "Sending..." : "Send Test Email"}
                </button>
                </div>
                {emailSuccess && <span className="text-sm text-green-600">{emailSuccess}</span>}
                {emailError && <span className="text-sm text-red-600">{emailError}</span>}
            </div>

            {/* Max Daily Messages Per User */}
            <div className="setting-card">
                <div className="flex items-center justify-between mb-4">
                    <h4 className="grey-900">Max Chat Sessions Per User</h4>
                    <div className="setting-card-icon">
                        <svg width="34" height="34" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <g clip-path="url(#clip0_576_3013)">
                                <path d="M29.0306 11.9052L28.6958 12.6754C28.6435 12.8008 28.5553 12.9079 28.4422 12.9833C28.3291 13.0586 28.1963 13.0989 28.0604 13.0989C27.9245 13.0989 27.7916 13.0586 27.6785 12.9833C27.5655 12.9079 27.4772 12.8008 27.4249 12.6754L27.0901 11.9052C26.5016 10.5427 25.4237 9.45023 24.0692 8.84343L23.0364 8.38213C22.9111 8.32449 22.8049 8.23213 22.7305 8.116C22.6561 7.99987 22.6166 7.86484 22.6166 7.72692C22.6166 7.58899 22.6561 7.45396 22.7305 7.33783C22.8049 7.2217 22.9111 7.12934 23.0364 7.07171L24.0121 6.63762C25.4006 6.01354 26.4969 4.8807 27.0752 3.47245L27.4194 2.64102C27.47 2.51223 27.5582 2.40166 27.6725 2.32372C27.7868 2.24579 27.922 2.2041 28.0604 2.2041C28.1987 2.2041 28.3339 2.24579 28.4482 2.32372C28.5625 2.40166 28.6507 2.51223 28.7013 2.64102L29.0456 3.47109C29.6232 4.8796 30.719 6.01293 32.1073 6.63762L33.0843 7.07307C33.2093 7.13087 33.315 7.22322 33.3892 7.3392C33.4633 7.45518 33.5027 7.58995 33.5027 7.7276C33.5027 7.86525 33.4633 8.00002 33.3892 8.116C33.315 8.23198 33.2093 8.32433 33.0843 8.38213L32.0502 8.84207C30.6959 9.44948 29.6185 10.5424 29.0306 11.9052ZM14.4526 4.92712H19.8957V7.64867H14.4526C12.2872 7.64867 10.2105 8.50888 8.67933 10.04C7.14816 11.5712 6.28796 13.6479 6.28796 15.8133C6.28796 20.7257 9.63819 23.9317 17.1742 27.3527V23.978H19.8957C22.0611 23.978 24.1378 23.1178 25.669 21.5866C27.2002 20.0554 28.0604 17.9787 28.0604 15.8133H30.7819C30.7819 18.7005 29.635 21.4695 27.5934 23.511C25.5519 25.5526 22.7829 26.6995 19.8957 26.6995V31.4622C13.0918 28.7407 3.56641 24.6584 3.56641 15.8133C3.56641 12.9261 4.71334 10.1572 6.7549 8.11562C8.79646 6.07406 11.5654 4.92712 14.4526 4.92712Z" fill="#DA291C" />
                            </g>
                            <defs>
                                <clipPath id="clip0_576_3013">
                                    <rect width="32.6586" height="32.6586" fill="white" transform="translate(0.844727 0.844727)" />
                                </clipPath>
                            </defs>
                        </svg>
                    </div>
                </div>
                <div className="form-group">
                    <label>Enter Number (leave empty for unlimited)</label>
                    <input
                        value={maxSessions}
                        onChange={(e) => setMaxSessions(e.target.value)}
                        placeholder="Enter number here"
                        className="form-control"
                    />
                </div>
                <div className="mt-3 flex gap-2 items-center">
                    <button
                        onClick={async () => {
                            try {
                                setSaving(true);
                                setSaveError(null);
                                setSaveSuccess(null);
                                const trimmed = maxSessions.trim();
                                const value = trimmed === "" ? null : Number(trimmed);
                                if (value !== null && (!Number.isFinite(value) || value < 0)) {
                                    throw new Error("Please enter a valid non-negative number or leave empty");
                                }
                                const res = await updateMaxDailyMessages(value);
                                setSaveSuccess("Saved");
                                setMaxSessions(res?.value == null ? "" : String(res.value));
                            } catch (e: any) {
                                setSaveError(e?.message || "Failed to save");
                            } finally {
                                setSaving(false);
                            }
                        }}
                        className="renegade-btn-primary mini-btn"
                        disabled={saving}
                    >
                        {saving ? "Saving..." : "Save"}
                    </button>
                    {saveSuccess && <span className="text-sm text-green-600">{saveSuccess}</span>}
                    {saveError && <span className="text-sm text-red-600">{saveError}</span>}
                    {loadError && <span className="text-sm text-red-600">{loadError}</span>}
                </div>
            </div>

            {/* Notification Testing Section */}
            {/* <AdminNotificationTester className="mt-6" /> */}
        </div>
    );
};

function ToggleRow({ label, enabled, onToggle }: { label: string; enabled: boolean; onToggle: () => void }) {
    return (
        <div className="flex items-center justify-between">
            <span className="text-16-reg grey-800">•&nbsp;&nbsp;{label}</span>
            <button
                type="button"
                onClick={onToggle}
                className={`global-swicth-toggle w-12 h-6 rounded-full transition-colors relative ${enabled ? 'toggle-on' : 'toggle-off'}`}
            >
                <span
                    className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`}
                />
            </button>
        </div>
    );
}

function SettingsIcon() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-settings-2"><path d="M20 7h-9" /><path d="M14 17H5" /><circle cx="17" cy="17" r="3" /><circle cx="7" cy="7" r="3" /></svg>
    );
}

export default AdminSettings;

